import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpenText,
  GameController,
  PencilLine,
  Trophy,
  Star,
  Heart,
  SpeakerHigh,
  SpeakerSlash,
  PuzzlePiece,
  ListBullets,
  Medal,
  Lightning,
  ArrowRight,
  House,
  User,
  SignIn,
  Books,
  UsersThree,
  Sparkle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import WordGame from "@/components/WordGame";
import StoryCreator from "@/components/StoryCreator";
import SentenceBuilder from "@/components/SentenceBuilder";
import FeelingsStation from "@/components/FeelingsStation";
import FollowLumiFingers from "@/components/FollowLumiFingers";
import { stopLumiAudio } from "@/lib/lumiAudio";
import { KidsLandingHero } from "@/components/kids/KidsLandingHero";
import { KidsActivityCards, KidsCalmPlay } from "@/components/kids/KidsLandingSections";
import { KidsFeelingsFeature } from "@/components/kids/KidsFeelingsFeature";
import { KidsJourneyRow, KidsFooter } from "@/components/kids/KidsLandingFooter";
import { useKidsSound } from "@/hooks/useKidsSound";
import { useLumi } from "@/components/LumiCharacter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KidsCorner = () => {
  const lumi = useLumi();
  const [soundOn, setSoundOn] = useKidsSound();

  const [activeSection, setActiveSection] = useState("home");
  // Direct link support: /kids-corner#feelings-station opens the Feelings Station tab
  useEffect(() => {
    const onHash = () => { if (window.location.hash === "#feelings-station") setActiveSection("feelings"); };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    const want = activeSection === "feelings" ? "#feelings-station" : "";
    if (window.location.hash !== want) window.history.replaceState(null, "", window.location.pathname + want);
  }, [activeSection]);
  // Landing screen: the big welcome Lumi is the only Lumi (no floating sticker/bubble). Switching tabs stops any sound.
  useEffect(() => {
    stopLumiAudio();
    if (activeSection === "home") lumi.hide();
    else if (activeSection !== "feelings" && activeSection !== "fingers") lumi.show();
    return () => { if (activeSection === "home") lumi.show(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  const [player, setPlayer] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginName, setLoginName] = useState("");
  const [leaderboard] = useState([]); // deprecated: public leaderboard removed for kids' privacy

  useEffect(() => {
    // Check for saved player
    const savedPlayer = localStorage.getItem("kids_player");
    if (savedPlayer) {
      setPlayer(JSON.parse(savedPlayer));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaderboard fetch removed: public leaderboard was retired for child privacy (COPPA).

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginName.trim()) return;

    try {
      const response = await axios.post(`${API}/kids/player`, { name: loginName.trim() });
      const playerData = response.data;
      setPlayer(playerData);
      localStorage.setItem("kids_player", JSON.stringify(playerData));
      setShowLogin(false);
      toast.success(`Welcome, ${playerData.name}! 🎉`);
    } catch (error) {
      toast.error("Oops! Something went wrong.");
    }
  };

  const handleLogout = () => {
    setPlayer(null);
    localStorage.removeItem("kids_player");
    toast.success("See you next time! 👋");
  };

  const updatePlayerScore = async (points, badge = null) => {
    if (!player) return;

    const updatedPlayer = {
      ...player,
      total_points: (player.total_points || 0) + points,
      badges: badge && !player.badges?.includes(badge) 
        ? [...(player.badges || []), badge] 
        : player.badges || []
    };

    setPlayer(updatedPlayer);
    localStorage.setItem("kids_player", JSON.stringify(updatedPlayer));
    if (badge && !player.badges?.includes(badge)) {
      lumi.say("You earned a new badge! 🏅", "flying", 2200); // brief Flying moment for a reward
    }

    try {
      await axios.put(`${API}/kids/player/${player.id}`, {
        total_points: updatedPlayer.total_points,
        badges: updatedPlayer.badges
      });
    } catch (error) {
      console.error("Failed to update score");
    }
  };

  const getBadgeIcon = (badge) => {
    const icons = {
      "first_game": "🎮",
      "word_master": "📚",
      "speed_demon": "⚡",
      "perfect_score": "🌟",
      "story_teller": "✨",
      "creative_genius": "🎨",
      "sentence_smith": "🧚‍♀️",
    };
    return icons[badge] || "🏆";
  };

  return (
    <div className="min-h-screen bg-[#fdfbf6] pb-36 sm:pb-8">
      {/* Fun Header */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#e6ddfa]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex justify-between items-center h-16 gap-3">
            <a href="/" className="flex items-center gap-1.5 min-w-0 shrink-0" data-testid="kids-nav-logo" aria-label="Embrace the Magic of Words home">
              <span aria-hidden="true" className="text-[#f2b544] text-sm leading-none hidden sm:inline">✦</span>
              <span className="font-nunito font-extrabold tracking-tight leading-[1.05] text-[#4a2e8a] text-xs sm:text-base">
                Embrace the<br /> <span className="text-[#6a3fc7] text-sm sm:text-lg">Magic of Words</span>
              </span>
            </a>

            <div className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2" data-testid="kids-nav-links">
              {[
                { id: "home", label: "Home", href: "/" },
                { id: "kids", label: "Kids Corner", href: "/kids-corner", active: true },
                { id: "resources", label: "Resources", href: "/resources" },
              ].map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  aria-current={l.active ? "page" : undefined}
                  className={`relative font-nunito font-bold text-base transition-colors ${l.active ? "text-[#4a2e8a] after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-[3px] after:rounded-full after:bg-[#2fb59f]" : "text-[#5b5572] hover:text-[#4a2e8a]"}`}
                  data-testid={`kids-nav-${l.id}`}
                >
                  {l.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <button
                type="button"
                onClick={() => setSoundOn(!soundOn)}
                aria-pressed={!soundOn}
                aria-label={soundOn ? "Turn sound off" : "Turn sound on"}
                className={`inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-full border font-nunito font-bold text-sm transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0] ${soundOn ? "border-[#d9ccf5] bg-white text-[#4a2e8a] hover:bg-[#f3eefc]" : "border-transparent bg-[#efe9fb] text-[#4a2e8a] hover:bg-[#e3d9f8]"}`}
                data-testid="kids-sound-toggle"
              >
                {soundOn ? <SpeakerHigh size={18} weight="bold" /> : <SpeakerSlash size={18} weight="bold" />}
                <span className="hidden sm:inline">{soundOn ? "Sound on" : "Sound off"}</span>
              </button>
              {player ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-[#fff6e0] px-3 py-1.5 rounded-full border border-[#f7dca0]">
                    <Star size={18} weight="fill" className="text-[#e9a400]" />
                    <span className="font-nunito font-extrabold text-[#4a2e8a]">{player.total_points || 0}</span>
                  </div>
                  <span className="font-nunito font-bold text-[#4a2e8a] hidden sm:block">
                    {player.name}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="font-nunito font-bold border border-[#d9ccf5] rounded-full text-[#4a2e8a] hover:bg-[#f3eefc]"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowLogin(true)}
                  variant="outline"
                  className="bg-white hover:bg-[#f3eefc] text-[#4a2e8a] font-nunito font-bold border border-[#4a2e8a] rounded-full h-10 px-3 sm:px-4"
                  data-testid="kids-join-open-btn"
                  aria-label="Parent sign in"
                >
                  <User size={20} weight="bold" className="sm:mr-2" />
                  <span className="hidden sm:inline">Parent sign in</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <AnimatePresence>
        {showLogin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLogin(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl border border-[#e6ddfa] shadow-[0_8px_30px_rgba(115,88,179,0.10)] p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-[#efe9fb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <User size={40} weight="duotone" className="text-[#6a3fc7]" />
                </div>
                <h2 className="font-nunito font-extrabold text-2xl text-[#4a2e8a]">
                  What's Your Name?
                </h2>
                <p className="font-nunito text-[#5b5572] mt-2">
                  Join to save your points & earn badges!
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  className="w-full font-nunito text-lg border-2 border-[#d9ccf5] rounded-2xl py-6 text-center focus-visible:ring-[#b79cf0]"
                  data-testid="kids-name-input"
                  maxLength={20}
                />
                <Button
                  type="submit"
                  className="w-full bg-[#6a3fc7] hover:bg-[#5a33ad] text-white font-nunito font-extrabold text-xl border-0 rounded-2xl py-6"
                  data-testid="kids-join-btn"
                >
                  Let's Play! 🎮
                </Button>
              </form>

              <p className="text-center mt-4 font-nunito text-sm text-[#5b5572]">
                Or play as guest (points won't be saved)
              </p>
              <Button
                variant="outline"
                onClick={() => setShowLogin(false)}
                className="w-full mt-2 font-nunito font-bold border border-[#d9ccf5] rounded-2xl text-[#4a2e8a] hover:bg-[#f3eefc]"
              >
                Play as Guest
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="relative font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#3d2378] tracking-tight text-center" data-testid="kids-page-heading">
          <span aria-hidden="true" className="text-[#f2b544] text-xl sm:text-2xl align-middle mr-3">✦</span>
          Kids Corner
          <span aria-hidden="true" className="text-[#f48fb1] text-xl sm:text-2xl align-middle ml-3">✦</span>
        </h1>
        <p className="font-nunito text-base md:text-lg text-[#5b5572] text-center mt-2 mb-6" data-testid="kids-page-subtitle">A little practice. A little magic. Your own pace.</p>
        <nav className="flex gap-3 overflow-x-auto pb-2 mb-8 -mx-1 px-1 snap-x md:justify-center" aria-label="Kids Corner activities" data-testid="kids-tabs" style={{ scrollbarWidth: "none" }}>
          {[
            { id: "wordplay", label: "Word Play", icon: PuzzlePiece, href: "/kids-vocab", tint: "text-[#2fb59f]" },
            { id: "sentence", label: "Sentence Builder", icon: ListBullets, tint: "text-[#2fa6e0]" },
            { id: "wordgame", label: "Word Games", icon: GameController, tint: "text-[#2fb59f]" },
            { id: "storycreator", label: "Story Magic", icon: PencilLine, tint: "text-[#f48fb1]" },
            { id: "feelings", label: "Feelings Station", icon: Heart, tint: "text-[#6a3fc7]" },
          ].map((t) => {
            const active = activeSection === t.id;
            const cls = `snap-start shrink-0 inline-flex items-center justify-center gap-2 h-11 px-4 sm:px-6 rounded-full font-nunito font-bold text-sm transition-colors focus-visible:ring-4 focus-visible:ring-[#b79cf0] ${active ? "bg-[#6a3fc7] text-white shadow-[0_6px_18px_rgba(106,63,199,0.30)]" : "bg-[#efe9fb] text-[#4a2e8a] hover:bg-[#e3d9f8]"}`;
            return t.href ? (
              <a key={t.id} href={t.href} className={cls} data-testid={`kids-tab-${t.id}`}><t.icon size={22} weight="fill" className={active ? "text-white" : t.tint} /> {t.label}</a>
            ) : (
              <button key={t.id} type="button" onClick={() => setActiveSection(t.id)} aria-current={active ? "page" : undefined} className={cls} data-testid={`kids-tab-${t.id}`}>
                <t.icon size={22} weight="fill" className={active ? "text-white" : t.tint} /> {t.label}
              </button>
            );
          })}
        </nav>
        {activeSection === "home" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <KidsLandingHero
              player={player}
              onChooseActivity={() => document.getElementById("kids-activities")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            />
            <KidsActivityCards onOpen={(id) => setActiveSection(id)} />
            <KidsFeelingsFeature onOpen={() => setActiveSection("feelings")} />
            <KidsCalmPlay onOpen={(id) => setActiveSection(id)} />
            <KidsJourneyRow player={player} onContinueProfile={() => setShowLogin(true)} />
            <KidsFooter />
          </motion.div>
        )}

        {activeSection === "wordgame" && (
          <WordGame 
            player={player} 
            onBack={() => setActiveSection("home")}
            onUpdateScore={updatePlayerScore}
          />
        )}

        {activeSection === "storycreator" && (
          <StoryCreator 
            player={player} 
            onBack={() => setActiveSection("home")}
            onUpdateScore={updatePlayerScore}
          />
        )}

        {activeSection === "feelings" && (
          <FeelingsStation onBack={() => setActiveSection("home")} />
        )}

        {activeSection === "fingers" && (
          <FollowLumiFingers onBack={() => setActiveSection("home")} />
        )}

        {activeSection === "sentence" && (
          <SentenceBuilder
            player={player}
            onBack={() => setActiveSection("home")}
            onUpdateScore={updatePlayerScore}
          />
        )}
      </div>

      {/* Back to Main Site */}
      <div className="fixed bottom-4 left-4">
        <a href="/">
          <Button 
            variant="outline"
            className="bg-white font-nunito font-bold border border-[#d9ccf5] text-[#4a2e8a] shadow-[0_8px_30px_rgba(115,88,179,0.15)] rounded-full hover:bg-[#f3eefc]"
          >
            <House size={20} weight="bold" className="mr-2" />
            Back to Main Site
          </Button>
        </a>
      </div>
    </div>
  );
};

export default KidsCorner;

/* ---------------- Shared subcomponents ---------------- */
